import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, Loader2, Package, ShoppingBag, XCircle, AlertTriangle, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  adminGetNotifications,
  adminGetUnreadCount,
  adminMarkNotificationRead,
  adminMarkAllNotificationsRead,
} from '@/lib/api';
import { cn } from '@/lib/utils';

const POLL_INTERVAL = 10_000; // 10 seconds

const TYPE_META = {
  new_order: { icon: ShoppingBag, color: 'text-green-700 bg-green-100' },
  status_update: { icon: Package, color: 'text-blue-700 bg-blue-100' },
  cancellation: { icon: XCircle, color: 'text-red-700 bg-red-100' },
  low_inventory: { icon: AlertTriangle, color: 'text-amber-700 bg-amber-100' },
  payment_failed: { icon: CreditCard, color: 'text-red-700 bg-red-100' },
};

function relative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const dropdownRef = useRef(null);

  // Poll unread count every 10s + refetch on cross-component refresh events
  useEffect(() => {
    let mounted = true;
    const fetchCount = async () => {
      try {
        const res = await adminGetUnreadCount();
        if (mounted) setUnread(res.data.unread || 0);
      } catch (err) {
        // Silent fail — admin may be logged out or offline; bell just stays stale
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('Notification poll failed:', err?.message);
        }
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    const onRefresh = () => fetchCount();
    window.addEventListener('notifications:refresh', onRefresh);
    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('notifications:refresh', onRefresh);
    };
  }, []);

  // Fetch latest items when dropdown opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    adminGetNotifications({ limit: 10 })
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkOne = async (id) => {
    await adminMarkNotificationRead(id);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnread((n) => Math.max(0, n - 1));
  };

  const handleMarkAll = async () => {
    setMarking(true);
    try {
      await adminMarkAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((v) => !v)}
        className="relative h-10 w-10 rounded-full hover:bg-muted"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center cart-badge"
            data-testid="notification-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-xl border border-border shadow-xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-bold">Notifications</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAll}
              disabled={marking || unread === 0}
              className="text-xs font-semibold"
              data-testid="mark-all-read-btn"
            >
              {marking ? <Loader2 className="w-3 h-3 animate-spin" /> : (<><CheckCheck className="w-3.5 h-3.5 mr-1" />Mark all read</>)}
            </Button>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-10 px-6">
                <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              items.map((n) => {
                const meta = TYPE_META[n.type] || TYPE_META.new_order;
                const Icon = meta.icon;
                return (
                  <div
                    key={n.id}
                    className={cn(
                      'flex gap-3 px-4 py-3 border-b border-border hover:bg-muted/40 transition',
                      !n.is_read && 'bg-primary/5'
                    )}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={cn('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0', meta.color)}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold leading-tight">{n.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">{relative(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                      <button
                        onClick={() => handleMarkOne(n.id)}
                        className="text-primary self-start p-1 hover:bg-primary/10 rounded"
                        title="Mark as read"
                        data-testid={`mark-read-${n.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="border-t border-border px-4 py-2.5 bg-muted/30 text-center">
            <Link
              to="/admin/notifications"
              className="text-sm font-semibold text-primary hover:underline"
              onClick={() => setOpen(false)}
              data-testid="view-all-notifications"
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
